import { useState } from 'react';
import {
  Row,
  Col,
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Badge,
  Alert,
  Table,
} from 'reactstrap';
import { StatusBadge } from '../../components/StatusBadge';
import { CopyChip } from '../../components/CopyChip';
import { RelativeTime } from '../../components/RelativeTime';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { QueryLoadingState, QueryErrorState } from '../../components/QueryStates';
import { useMe } from '../../me/MeProvider';
import { useBrand } from '../../brand/BrandProvider';
import { isApiError } from '../../api/errors';
import {
  useUpdateMyProfile,
  useChangeMyPassword,
  useMySessions,
  useRevokeSession,
} from './useMe';

export function MeProfilePage() {
  const { me } = useMe();
  const brand = useBrand();

  if (!me) {
    return <Alert color="warning">Profil noch nicht geladen.</Alert>;
  }

  return (
    <div>
      <Row className="g-3 mb-3">
        <Col md="7">
          <StammdatenCard user={me.user} />
        </Col>
        <Col md="5">
          <CurrentBrandCard brand={brand} />
        </Col>
      </Row>
      <ChangePasswordCard />
      <SessionsCard />
    </div>
  );
}

function StammdatenCard({ user }) {
  const [displayName, setDisplayName] = useState(user.displayName ?? '');
  const [error, setError] = useState(null);
  const update = useUpdateMyProfile();
  const dirty = displayName.trim() !== (user.displayName ?? '');

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    try {
      await update.mutateAsync({ displayName: displayName.trim() });
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Speichern fehlgeschlagen.');
    }
  };

  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Stammdaten</CardTitle>
        <Form onSubmit={onSubmit}>
          <Row className="g-2 mb-3">
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">User-ID</Label>
                <div className="fw-semibold">{user.id}</div>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">Kundennummer</Label>
                <div className="fw-semibold">
                  <CopyChip value={user.customerNumber} label="Kundennummer" />
                </div>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">E-Mail</Label>
                <div className="fw-semibold">
                  {user.email}
                  {!user.emailVerified && (
                    <Badge color="warning" pill className="ms-2" style={{ fontSize: 10 }}>
                      unverified
                    </Badge>
                  )}
                </div>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">Rolle</Label>
                <div>
                  <StatusBadge value={user.role} />
                </div>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">Account erstellt</Label>
                <div className="small"><RelativeTime iso={user.createdAt} /></div>
              </FormGroup>
            </Col>
            <Col sm="6">
              <FormGroup>
                <Label className="small text-muted text-uppercase">Letzter Login</Label>
                <div className="small"><RelativeTime iso={user.lastLoginAt} /></div>
              </FormGroup>
            </Col>
          </Row>

          <hr />

          <FormGroup>
            <Label>Anzeigename</Label>
            <Input
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              maxLength={100}
              disabled={update.isPending}
            />
            <small className="text-muted">
              Wird in Audit-Log, Customer-Replies und Operator-Sichtbarkeit verwendet.
            </small>
          </FormGroup>

          {error && <Alert color="danger" className="mb-2">{error}</Alert>}

          <Button color="primary" size="sm" type="submit" disabled={!dirty || update.isPending}>
            {update.isPending ? 'Speichern…' : 'Speichern'}
          </Button>
        </Form>
      </CardBody>
    </Card>
  );
}

function CurrentBrandCard({ brand }) {
  const current = brand.currentBrand;
  if (!current) {
    return (
      <Card className="h-100">
        <CardBody>
          <CardTitle tag="h5">Aktive Brand</CardTitle>
          <p className="text-muted small mb-0">Keine Brand ausgewählt.</p>
        </CardBody>
      </Card>
    );
  }

  return (
    <Card className="h-100">
      <CardBody>
        <CardTitle tag="h5">Aktive Brand</CardTitle>
        <div className="d-flex align-items-center gap-3 mb-3">
          {current.logoUrl && (
            <img src={current.logoUrl} alt="" style={{ width: 48, height: 48, objectFit: 'contain' }} />
          )}
          <div>
            <div className="fw-bold">{current.displayName}</div>
            <CopyChip value={current.brandOrigin} label="Brand-Origin" />
          </div>
        </div>
        <p className="small text-muted mb-3">
          Du hast Zugang zu {brand.brands.length} Brand{brand.brands.length === 1 ? '' : 's'}.
          Wechseln über das Header-Dropdown.
        </p>
        <div className="d-flex flex-wrap gap-1">
          {brand.brands.map((b) => (
            <Badge
              key={b.brandOrigin}
              color={b.brandOrigin === current.brandOrigin ? 'primary' : 'secondary'}
              pill
            >
              {b.slug}
            </Badge>
          ))}
        </div>
      </CardBody>
    </Card>
  );
}

function ChangePasswordCard() {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [repeatPassword, setRepeatPassword] = useState('');
  const [error, setError] = useState(null);
  const change = useChangeMyPassword();

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    if (newPassword.length < 12) {
      setError('Neues Passwort muss mindestens 12 Zeichen haben.');
      return;
    }
    if (newPassword !== repeatPassword) {
      setError('Wiederholung stimmt nicht überein.');
      return;
    }
    try {
      await change.mutateAsync({ currentPassword, newPassword });
      setCurrentPassword('');
      setNewPassword('');
      setRepeatPassword('');
    } catch (err) {
      setError(isApiError(err) ? err.message : 'Passwort-Änderung fehlgeschlagen.');
    }
  };

  return (
    <Card className="mb-3">
      <CardBody>
        <CardTitle tag="h5">Passwort ändern</CardTitle>
        <Form onSubmit={onSubmit}>
          <Row>
            <Col md="4">
              <FormGroup>
                <Label className="small">Aktuelles Passwort</Label>
                <Input
                  type="password"
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  autoComplete="current-password"
                  required
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="small">Neues Passwort</Label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={12}
                />
              </FormGroup>
            </Col>
            <Col md="4">
              <FormGroup>
                <Label className="small">Wiederholen</Label>
                <Input
                  type="password"
                  value={repeatPassword}
                  onChange={(e) => setRepeatPassword(e.target.value)}
                  autoComplete="new-password"
                  required
                  minLength={12}
                />
              </FormGroup>
            </Col>
          </Row>
          <small className="text-muted d-block mb-2">
            Mindestens 12 Zeichen. Alle aktiven Sessions außer der aktuellen werden
            nach erfolgreichem Wechsel beendet (Server-Verhalten).
          </small>
          {error && <Alert color="danger" className="mb-2">{error}</Alert>}
          <Button
            color="primary"
            size="sm"
            type="submit"
            disabled={
              change.isPending
              || !currentPassword
              || !newPassword
              || !repeatPassword
            }
          >
            {change.isPending ? 'Ändern…' : 'Passwort setzen'}
          </Button>
        </Form>
      </CardBody>
    </Card>
  );
}

function SessionsCard() {
  const query = useMySessions();
  const revoke = useRevokeSession();
  const [pendingRevoke, setPendingRevoke] = useState(null);

  return (
    <Card>
      <CardBody>
        <div className="d-flex justify-content-between align-items-center mb-3">
          <CardTitle tag="h5" className="mb-0">Aktive Sessions</CardTitle>
          <Button color="primary" outline size="sm" onClick={() => query.refetch()}>
            ↻ Aktualisieren
          </Button>
        </div>

        {query.isLoading && <QueryLoadingState />}
        {query.isError && <QueryErrorState error={query.error} onRetry={() => query.refetch()} />}
        {query.data?.unavailable && (
          <Alert color="info" className="small mb-0">
            Session-Endpoint ist auf diesem Core-Deploy nicht aktiv
            (<code>GET /v1/operator/me/sessions</code> → 404).
          </Alert>
        )}
        {query.data && !query.data.unavailable && (
          <Table size="sm" className="mb-0 align-middle">
            <thead>
              <tr>
                <th>Session</th>
                <th>IP / Standort</th>
                <th>User-Agent</th>
                <th style={{ width: 140 }}>Letzte Aktivität</th>
                <th style={{ width: 130 }}>Erstellt</th>
                <th style={{ width: 110 }} />
              </tr>
            </thead>
            <tbody>
              {query.data.sessions.map((s) => (
                <tr key={s.id}>
                  <td>
                    <code className="small">{s.id}</code>
                    {s.current && (
                      <Badge color="success" pill className="ms-2" style={{ fontSize: 10 }}>
                        diese Session
                      </Badge>
                    )}
                  </td>
                  <td className="small">
                    <code>{s.ip}</code>
                    <div className="text-muted">{s.location}</div>
                  </td>
                  <td className="small text-muted" style={{ maxWidth: 240, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={s.userAgent}>
                    {s.userAgent}
                  </td>
                  <td><RelativeTime iso={s.lastActivityAt} /></td>
                  <td><RelativeTime iso={s.createdAt} /></td>
                  <td className="text-end">
                    {!s.current && (
                      <Button
                        color="danger"
                        outline
                        size="sm"
                        onClick={() => setPendingRevoke(s)}
                        disabled={revoke.isPending}
                      >
                        Beenden
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}

        <ConfirmDialog
          isOpen={!!pendingRevoke}
          title="Session beenden?"
          body={
            pendingRevoke ? (
              <>
                Session <code>{pendingRevoke.id}</code> ({pendingRevoke.ip}, {pendingRevoke.location})
                wird sofort ausgeloggt. Wenn jemand sich gerade von dort eingeloggt hat — okay,
                aber er muss neu einloggen.
              </>
            ) : null
          }
          confirmLabel="Beenden"
          confirmColor="danger"
          busy={revoke.isPending}
          onConfirm={async () => {
            await revoke.mutateAsync({ id: pendingRevoke.id });
            setPendingRevoke(null);
          }}
          onCancel={() => setPendingRevoke(null)}
        />
      </CardBody>
    </Card>
  );
}
